import React, { useRef, useEffect } from 'react';
import { X, Camera, Image as ImageIcon, Trash2 } from 'lucide-react';
import { compressImage } from './utils/imageUtils';

function CameraModal({ open, onClose, images, onImagesChange }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup stream on unmount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const openCamera = async () => {
    // Stop any existing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    // Try different camera constraints
    const constraintsList = [
      { video: { facingMode: { exact: 'environment' } } },
      { video: { facingMode: 'environment' } },
      { video: true }
    ];

    let stream = null;
    for (let constraint of constraintsList) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraint);
        break;
      } catch (e) {
        continue;
      }
    }

    if (!stream) {
      alert('No se pudo acceder a la cámara.');
      return;
    }

    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(console.error);
    }
  };

  const capturePhoto = async () => {
    const stream = streamRef.current;
    const video = videoRef.current;
    if (!stream || !video) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);

    canvas.toBlob(async (blob) => {
      if (!blob) return;
      try {
        const compressed = await compressImage(blob, 0.6, 1000, 1000);
        onImagesChange([...images, compressed]);
      } catch (err) {
        console.error('Error compressing image:', err);
      }
    }, 'image/jpeg');

    // Stop camera after capture
    stream.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressed = await compressImage(file, 0.6, 1000, 1000);
      onImagesChange([...images, compressed]);
    } catch (err) {
      console.error('Error compressing image:', err);
      onImagesChange([...images, file]);
    } finally {
      e.target.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    onImagesChange(images.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 2000 }}>
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Imágenes</h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Camera Preview */}
          {streamRef.current && (
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              <video
                ref={videoRef}
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            {!streamRef.current ? (
              <button
                onClick={openCamera}
                className="flex-1 bg-brand-gold text-white py-3 rounded-lg font-semibold hover:bg-brand-gold/90 transition-colors flex items-center justify-center gap-2"
              >
                <Camera className="w-5 h-5" />
                Abrir Cámara
              </button>
            ) : (
              <button
                onClick={capturePhoto}
                className="flex-1 bg-brand-green text-white py-3 rounded-lg font-semibold hover:bg-brand-green/90 transition-colors flex items-center justify-center gap-2"
              >
                <Camera className="w-5 h-5" />
                Capturar Foto
              </button>
            )}

            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 bg-gray-100 text-gray-900 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
            >
              <ImageIcon className="w-5 h-5" />
              Galería
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Image Grid */}
          {images.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Imágenes capturadas ({images.length})
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {images.map((blob, index) => {
                  const url = URL.createObjectURL(blob);
                  return (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Imagen ${index + 1}`}
                        className="w-full aspect-square object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        onClick={() => handleRemoveImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Done Button */}
          <button
            onClick={handleClose}
            className="w-full bg-brand-gold text-white py-3 rounded-lg font-semibold hover:bg-brand-gold/90 transition-colors"
          >
            Listo
          </button>
        </div>
      </div>
    </div>
  );
}

export default CameraModal;